import { apiClient } from '@/services/api';
import type {
  EveryoneFeedResponse,
  ReferralHideResponse,
  ReferralsListResponse,
} from '@/types/referral';

/**
 * 'recent' = signed_up_at desc (the server default). 'last_update' = the
 * deal screen's fixed per-tab order: Started stalest-first, Paid newest,
 * Not started newest signup first.
 */
export type EveryoneFeedSort = 'recent' | 'last_update';

/**
 * A feed stage filter. The v3 pair (in_progress | paid) plus the deal
 * screen's tabs (started | not_started, which split in_progress), and
 * `removed` for the hidden side. Falsy = no filter.
 */
export type EveryoneFeedStage = 'in_progress' | 'paid' | 'started' | 'not_started' | 'removed';

export interface EveryoneFeedQuery {
  dealId?: number | null;
  /** One stage; the server also accepts a comma-separated list. */
  stage?: EveryoneFeedStage | null;
  /** Substring search over the row name (server lowercases both sides). */
  search?: string | null;
  sort?: EveryoneFeedSort | null;
}

export const referralsService = {
  /**
   * GET /api/v1/referrals — full list of the current user's referrals
   * with each referee's Actions inlined.
   */
  async list(): Promise<ReferralsListResponse> {
    return apiClient.get<ReferralsListResponse>('/api/v1/referrals');
  },

  /**
   * GET /api/v1/referrals/everyone_feed — unified "Who's clicked" feed
   * for the Refer tab. v3 (2026-05-11) collapsed Outreach + Funnel into
   * one feed; every signed-up referee appears here. Server filters items
   * by stage/search AND returns chip_counts so the chip row stays
   * accurate when items are filtered.
   */
  async everyoneFeed(query: EveryoneFeedQuery = {}): Promise<EveryoneFeedResponse> {
    const params = new URLSearchParams();
    if (query.dealId != null) params.set('deal_id', String(query.dealId));
    if (query.stage) params.set('stage', query.stage);
    if (query.search?.trim()) params.set('search', query.search.trim());
    if (query.sort) params.set('sort', query.sort);
    const qs = params.toString();
    const path = qs ? `/api/v1/referrals/everyone_feed?${qs}` : '/api/v1/referrals/everyone_feed';
    return apiClient.get<EveryoneFeedResponse>(path);
  },

  /**
   * POST /api/v1/referrals/:id/hide — remove a dead lead from the
   * ambassador's list. Soft hide: attribution and earnings are untouched,
   * and the row returns automatically if the referee ever converts.
   *
   * Rejects with 422 `remove_paid_referral` if the referral is already
   * paid. The UI hides the affordance on paid rows, but the server is the
   * gate — a conversion can land between render and tap.
   */
  async hide(referralId: number, dealId: number | null = null): Promise<ReferralHideResponse> {
    return apiClient.post<ReferralHideResponse>(hidePath(referralId, 'hide', dealId));
  },

  /** POST /api/v1/referrals/:id/unhide — undo a removal. */
  async unhide(referralId: number, dealId: number | null = null): Promise<ReferralHideResponse> {
    return apiClient.post<ReferralHideResponse>(hidePath(referralId, 'unhide', dealId));
  },
};

/**
 * `deal_id` scopes the chip_counts the server sends back to the screen that
 * asked. The deal screen's tabs read those counts straight into its tabs;
 * without the scope the response carries account-wide numbers and the tabs
 * are wrong until the next feed fetch.
 */
function hidePath(referralId: number, verb: 'hide' | 'unhide', dealId: number | null): string {
  const base = `/api/v1/referrals/${referralId}/${verb}`;
  return dealId != null ? `${base}?deal_id=${dealId}` : base;
}
